import { forwardRef, useImperativeHandle, useRef } from 'react';
import { CountrySelector, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

type PhoneFieldProps = {
  /** Full E.164 digits, no "+" (e.g. "919876543210"). Empty string when untouched. */
  value: string;
  onChange: (digits: string) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
};

// Maps the library's CSS custom properties onto our design tokens so the country dropdown
// matches the rest of the app instead of the library's own default look.
const themeVars = {
  '--react-international-phone-height': '100%',
  '--react-international-phone-border-radius': '0px',
  '--react-international-phone-font-size': '16px',
  '--react-international-phone-background-color': 'transparent',
  '--react-international-phone-text-color': 'var(--color-ink)',
  '--react-international-phone-border-color': 'transparent',
  '--react-international-phone-country-selector-border-color': 'transparent',
  '--react-international-phone-country-selector-background-color-hover': 'var(--color-canvas)',
  '--react-international-phone-country-selector-arrow-color': 'var(--color-muted)',
  '--react-international-phone-dropdown-item-background-color': '#ffffff',
  '--react-international-phone-dropdown-item-text-color': 'var(--color-ink)',
  '--react-international-phone-dropdown-item-dial-code-color': 'var(--color-muted)',
  '--react-international-phone-selected-dropdown-item-background-color': 'var(--color-canvas)',
  '--react-international-phone-dropdown-shadow': 'var(--shadow-card)',
} as React.CSSProperties;

export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(({ value, onChange, className = '', required, disabled }, ref) => {
  const localRef = useRef<HTMLInputElement | null>(null);
  useImperativeHandle(ref, () => localRef.current as HTMLInputElement);

  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'in',
    value: value ? `+${value}` : '',
    disableDialCodeAndPrefix: true,
    inputRef: localRef,
    onChange: (data) => onChange(data.phone.replace(/\D/g, '')),
  });

  return (
    <div style={themeVars} className={`flex min-h-11 w-full items-stretch rounded-xl border bg-white transition-colors focus-within:border-brand-500 ${disabled ? 'opacity-60' : ''} ${className}`}>
      <CountrySelector
        selectedCountry={country.iso2}
        onSelect={(c) => setCountry(c.iso2)}
        disabled={disabled}
        className="shrink-0 rounded-l-xl border-r"
        buttonClassName="flex h-full items-center gap-1.5 rounded-l-xl px-3"
        dropdownStyleProps={{ className: 'w-64 rounded-xl border' }}
      />
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        required={required}
        disabled={disabled}
        placeholder="98765 43210"
        value={inputValue}
        onChange={handlePhoneValueChange}
        className="min-w-0 flex-1 rounded-r-xl bg-transparent px-3 text-base text-ink outline-none placeholder:text-muted disabled:cursor-not-allowed"
      />
    </div>
  );
});
PhoneField.displayName = 'PhoneField';
